import { useEffect } from 'react';
import { OG_HOME } from '../lib/og';

/**
 * SEO Component - Gestiona meta tags dinámicos para SEO
 * Compatible con React 19 sin dependencias externas
 */
export default function SEO({
  title,
  description,
  image,
  url,
  type = 'website',
  publishedAt,
  modifiedAt,
  author = 'Equipo GeoVis',
  tags = [],
  articleSection = null,
  sources = [],
  wordCount = null,
  siteName = 'GeoVis',
  twitterHandle = '@geovis_es',
  locale = 'es_ES',
  hreflangs = null,
  noindex = false
}) {
  const baseUrl = 'https://www.geovis.es';
  const fullUrl = url ? `${baseUrl}${url}` : baseUrl;
  const fullImage = image ? (image.startsWith('http') ? image : `${baseUrl}${image}`) : `${baseUrl}${OG_HOME}`;
  // Si el título ya nombra la marca no se le añade « | GeoVis» (evita «GeoVis … | GeoVis»).
  const fullTitle = title ? (title.includes(siteName) ? title : `${title} | ${siteName}`) : siteName;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper function to set meta tag
    const setMetaTag = (name, content, isProperty = false) => {
      if (!content) return;

      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }

      meta.setAttribute('content', content);
    };

    // Helper function to set link tag
    const setLinkTag = (rel, href) => {
      if (!href) return;

      let link = document.querySelector(`link[rel="${rel}"]`);

      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }

      link.setAttribute('href', href);
    };

    // Robots: por defecto el index.html estático trae "index, follow". En una SPA
    // hay que actualizarlo explícitamente por ruta para poder marcar noindex (404,
    // páginas técnicas) y para reponer "index, follow" al navegar a una indexable.
    setMetaTag('robots', noindex ? 'noindex, follow' : 'index, follow');

    // Basic meta tags
    setMetaTag('description', description);
    const authorName = typeof author === 'object' && author?.name ? author.name : author;
    setMetaTag('author', authorName);
    if (tags.length > 0) {
      setMetaTag('keywords', tags.join(', '));
    }

    // Canonical URL
    setLinkTag('canonical', fullUrl);

    // Hreflang alternates: borramos TODOS los previos — tanto los inyectados por este
    // componente como el bloque estático de public/index.html (que solo es válido para
    // la home; heredarlo en /demo, /blog, etc. declara alternativas de idioma falsas).
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    if (Array.isArray(hreflangs) && hreflangs.length > 0) {
      hreflangs.forEach(({ hreflang, href }) => {
        if (!hreflang || !href) return;
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', hreflang);
        link.setAttribute('href', href);
        link.setAttribute('data-seo-hreflang', 'true');
        document.head.appendChild(link);
      });
    }

    // Open Graph tags
    // Al compartir el enlace se ve este título: siempre con la marca.
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:image', fullImage, true);
    setMetaTag('og:url', fullUrl, true);
    setMetaTag('og:type', type === 'article' ? 'article' : 'website', true);
    setMetaTag('og:site_name', siteName, true);
    setMetaTag('og:locale', locale, true);

    // Article-specific Open Graph tags
    if (type === 'article') {
      setMetaTag('article:published_time', publishedAt, true);
      if (modifiedAt) {
        setMetaTag('article:modified_time', modifiedAt, true);
      }
      setMetaTag('article:author', authorName, true);
      tags.forEach((tag, index) => {
        setMetaTag(`article:tag:${index}`, tag, true);
      });
    }

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:site', twitterHandle);
    setMetaTag('twitter:title', fullTitle);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', fullImage);

    // Schema.org JSON-LD
    let schema;
    if (type === 'article') {
      // Determinar si el autor es un objeto (Person) o string (Organization fallback)
      const authorSchema = typeof author === 'object' && author.name
        ? {
            '@type': 'Person',
            name: author.name,
            jobTitle: author.role,
            worksFor: { '@id': `${baseUrl}/#organization` }
          }
        : {
            '@type': 'Organization',
            name: author,
            url: baseUrl
          };

      schema = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: title,
        image: fullImage,
        datePublished: publishedAt,
        dateModified: modifiedAt || publishedAt,
        author: authorSchema,
        publisher: {
          '@type': 'Organization',
          '@id': `${baseUrl}/#organization`,
          name: siteName,
          logo: {
            '@type': 'ImageObject',
            url: `${baseUrl}/geovis-logo.png`
          }
        },
        description: description,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': fullUrl
        },
        keywords: tags.join(', '),
        inLanguage: locale.replace('_', '-'),
        ...(articleSection ? { articleSection } : {}),
        ...(wordCount ? { wordCount } : {}),
        // `citation` expone las fuentes oficiales del post. Es la señal que los
        // motores generativos usan para decidir si un pasaje es citable.
        ...(sources.length
          ? {
              citation: sources.map((s) => ({
                '@type': 'CreativeWork',
                name: s.name,
                url: s.url
              }))
            }
          : {})
      };
    } else {
      // WebPage por ruta (el nodo WebSite estable vive en public/index.html).
      // Antes se emitía un WebSite cuya description cambiaba por página —
      // señales de entidad inconsistentes para los buscadores.
      schema = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': fullUrl,
        name: fullTitle,
        url: fullUrl,
        description: description,
        inLanguage: locale.replace('_', '-'),
        isPartOf: { '@id': `${baseUrl}/#website` },
        publisher: { '@id': `${baseUrl}/#organization` }
      };
    }

    // Add or update JSON-LD script
    let scriptTag = document.querySelector('script[data-seo-schema]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-seo-schema', 'true');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(schema);
  }, [
    fullTitle,
    description,
    fullImage,
    fullUrl,
    type,
    publishedAt,
    modifiedAt,
    author,
    tags,
    articleSection,
    sources,
    wordCount,
    siteName,
    twitterHandle,
    locale,
    title,
    baseUrl,
    hreflangs,
    noindex
  ]);

  // No renderiza nada - todo se maneja con useEffect
  return null;
}
